/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PromptPreviewModal.tsx
 🗓️ Created: 2026-01-17
 🧩 Description:
    Modal to preview the system prompt generated from agent configuration.
    Follows OpenAI Realtime Prompting Guide recommendations.
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Copy, Check, RefreshCw } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { generatePromptFromFormData, estimateTokens, type PromptFormData } from '../utils/promptGenerator';

interface PromptPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    formData: PromptFormData & {
        system_prompt?: string;
    };
    onApplyGeneratedPrompt?: (prompt: string) => void;
    generatorFn?: (data: any) => string;
}

export default function PromptPreviewModal({ isOpen, onClose, formData, onApplyGeneratedPrompt, generatorFn }: PromptPreviewModalProps) {
    const { t } = useI18n();
    const { showSuccess } = useNotifications();
    const [copied, setCopied] = useState(false);

    // Use shared utility for prompt generation
    const generatePrompt = (): string => {
        if (generatorFn) return generatorFn(formData);
        return generatePromptFromFormData(formData);
    };

    const handleCopy = async () => {
        const prompt = generatePrompt();
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = prompt;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleApplyPrompt = () => {
        const prompt = generatePrompt();
        if (onApplyGeneratedPrompt) {
            onApplyGeneratedPrompt(prompt);
            showSuccess(t('aiAgents.messages.promptApplied') || 'Prompt applied to System Prompt field');
            onClose();
        }
    };

    const prompt = generatePrompt();
    const hasExistingPrompt = formData.system_prompt && formData.system_prompt.trim().length > 0;
    // Always show the generated prompt from form data (not the saved one)
    const displayPrompt = prompt;
    const isFromSaved = false; // We always show the template now

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[2500]"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 z-[2500] flex items-center justify-center p-4 pt-20">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: 'spring', duration: 0.3 }}
                            className="bg-card border border-border rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/10 rounded-lg">
                                        <Eye className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground">
                                            {t('aiAgents.promptPreview.title') || 'Agent Prompt Preview'}
                                        </h3>
                                        <span className="text-xs text-muted-foreground flex items-center gap-2">
                                            <span className={isFromSaved ? 'text-green-500' : 'text-amber-500'}>●</span>
                                            <span>{isFromSaved ? (t('aiAgents.promptPreview.saved') || 'Saved') : (t('aiAgents.promptPreview.template') || 'Template')}</span>
                                            <span className="text-muted-foreground">|</span>
                                            <span className="font-mono">{estimateTokens(displayPrompt!).toLocaleString()}</span>
                                            <span>{t('aiAgents.promptPreview.tokens') || 'tokens'}</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleCopy}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-accent hover:bg-accent/80 rounded-md transition-colors"
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="h-4 w-4 text-green-500" />
                                                <span className="text-green-500">{t('common.copied') || 'Copied!'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-4 w-4" />
                                                <span>{t('common.copy') || 'Copy'}</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="text-sm text-muted-foreground mb-4">
                                    {isFromSaved
                                        ? (t('aiAgents.promptPreview.savedPromptDescription') || 'This is the saved system prompt that will be used during real-time voice conversations.')
                                        : (t('aiAgents.promptPreview.description') || 'This is a preview template generated from your agent configuration. Apply it to use as the System Prompt.')
                                    }
                                </div>
                                <pre className="p-4 bg-muted/30 border border-border rounded-lg overflow-x-auto whitespace-pre-wrap text-sm font-mono text-foreground">
                                    {displayPrompt}
                                </pre>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}
